import { useState } from 'react';
import { axiosClient } from '../../../library/Service/axiosClient';
import Swal from 'sweetalert2';

const MAX_ROWS = 2000;
const PROFILE_SAMPLE_URL = 'https://company-logo-list.s3.ap-south-1.amazonaws.com/Student_update_sample.xlsx';
const COMPANY_SAMPLE_URL = 'https://company-logo-list.s3.ap-south-1.amazonaws.com/company_data_update_sample.xlsx';
const ACHIEVEMENT_SAMPLE_URL =
    'https://company-logo-list.s3.ap-south-1.amazonaws.com/achievement_data_update_sample.xlsx';

const isValidEmail = (value: any): boolean => {
    const email = String(value || '').trim();
    if (!email || email === 'undefined' || email === 'null') return false;
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};

const getLoggedInAdminEmail = (): string => {
    const candidates: any[] = [];

    try {
        const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
        candidates.push(
            sessionUser?.registeredEmailId,
            sessionUser?.registered_email_id,
            sessionUser?.email,
            sessionUser?.emailId,
            sessionUser?.userName,
            sessionUser?.username
        );
    } catch (_) {}

    try {
        const localUser = JSON.parse(localStorage.getItem('user') || '{}');
        candidates.push(
            localUser?.registeredEmailId,
            localUser?.registered_email_id,
            localUser?.email,
            localUser?.emailId,
            localUser?.userName,
            localUser?.username
        );
    } catch (_) {}

    for (const value of candidates) {
        if (isValidEmail(value)) return String(value).trim();
    }
    return '';
};

type UploadMode = 'profile' | 'company' | 'achievement';

const MODE_CONFIG: Record<
    UploadMode,
    {
        endpoint: string;
        sampleUrl: string;
        sampleLabel: string;
        submitLabel: string;
        loadingLabel: string;
        summaryTitle: string;
        showInserted: boolean;
    }
> = {
    profile: {
        endpoint: '/raw-user/update-alumni-spread-sheet',
        sampleUrl: PROFILE_SAMPLE_URL,
        sampleLabel: 'Download Alumni Update Sample File',
        submitLabel: 'Update',
        loadingLabel: 'Updating...',
        summaryTitle: 'Update Summary',
        showInserted: false,
    },
    company: {
        endpoint: '/raw-user/update-alumni-company-spread-sheet',
        sampleUrl: COMPANY_SAMPLE_URL,
        sampleLabel: 'Download Company Update Sample File',
        submitLabel: 'Update Companies',
        loadingLabel: 'Updating companies...',
        summaryTitle: 'Company Update Summary',
        showInserted: true,
    },
    achievement: {
        endpoint: '/raw-user/update-alumni-achievement-spread-sheet',
        sampleUrl: ACHIEVEMENT_SAMPLE_URL,
        sampleLabel: 'Download Achievement Update Sample File',
        submitLabel: 'Update Achievements',
        loadingLabel: 'Updating achievements...',
        summaryTitle: 'Achievement Update Summary',
        showInserted: true,
    },
};

const UpdateAlumniLayout = (_props: any) => {
    const [mode, setMode] = useState<UploadMode>('profile');
    const [file, setFile] = useState<File | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [responseMessage, setResponseMessage] = useState<{
        updated: number;
        inserted?: number;
        failed: number;
        emailReport?: { success?: boolean; to?: string; message?: string };
    } | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const modeConfig = MODE_CONFIG[mode];

    const resetResultState = () => {
        setResponseMessage(null);
        setErrorMessage(null);
        setUploadProgress(0);
    };

    const handleModeChange = (next: UploadMode) => {
        setMode(next);
        setFile(null);
        resetResultState();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFile(e.target.files[0]);
            resetResultState();
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!file) return;

        const reportToEmail = getLoggedInAdminEmail();
        if (!reportToEmail) {
            Swal.fire({
                icon: 'warning',
                title: 'Admin email missing',
                text: 'Could not find logged-in admin email for the report. Please re-login and try again.',
            });
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('reportToEmail', reportToEmail);
        const url = `${process.env.REACT_APP_INTERNAL_ADMIN_SERVER}${modeConfig.endpoint}`;

        try {
            setIsLoading(true);
            setUploadProgress(0);
            setErrorMessage(null);

            const uploadResponse = await axiosClient().post(url, formData, {
                onUploadProgress: (progressEvent) => {
                    if (progressEvent?.loaded !== undefined && progressEvent?.total !== undefined) {
                        setUploadProgress(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                    }
                },
            });

            const {
                updated,
                inserted,
                failed,
                message,
                success,
                emailReport,
            } = uploadResponse.data || {};

            if (success === false && message) {
                setErrorMessage(message);
            }
            setResponseMessage({
                updated: updated ?? 0,
                inserted: inserted ?? 0,
                failed: failed ?? 0,
                emailReport,
            });
        } catch (error: any) {
            console.error('Error uploading alumni update file:', error);
            setErrorMessage(error?.response?.data?.message || 'Error uploading file');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container max-1140 px-lg-0 overflow-hidden">
            <div className="row">
                <div className="col-md-12 mb-4">
                    <div className="container mt-5">
                        <h2 className="mb-4">Update Alumni Data</h2>

                        <ul className="nav nav-tabs mb-3">
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link ${mode === 'profile' ? 'active' : ''}`}
                                    onClick={() => handleModeChange('profile')}>
                                    Update Profile
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link ${mode === 'company' ? 'active' : ''}`}
                                    onClick={() => handleModeChange('company')}>
                                    Update / Add Companies
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    type="button"
                                    className={`nav-link ${mode === 'achievement' ? 'active' : ''}`}
                                    onClick={() => handleModeChange('achievement')}>
                                    Update / Add Achievements
                                </button>
                            </li>
                        </ul>

                        {mode === 'profile' && (
                            <div className="alert alert-info">
                                <h5>Please Note:</h5>
                                <ul>
                                    <li>The file must be in Excel format (.xlsx). Max {MAX_ROWS} rows.</li>
                                    <li>
                                        <strong>Mandatory columns:</strong> uuid, institute_id, Email_id
                                    </li>
                                    <li>
                                        Each row must match an existing alumni using uuid, institute_id, and Email_id.
                                    </li>
                                    <li>
                                        Only non-empty cells are updated. Empty cells leave existing DB values
                                        unchanged.
                                    </li>
                                    <li>
                                        Use numeric IDs for: current_country, current_city, ug_college_id,
                                        pg_college_id, pg_college_id2, pg_college_id3, and program (program_id).
                                        Free-text names go in ug_college_name / pg_college_name / pg_college_name2 /
                                        pg_college_name3.
                                    </li>
                                    <li>
                                        Optional profile fields: <code>first_name</code>, <code>last_name</code>,{' '}
                                        <code>career_track</code>, <code>profile_image_url</code>.
                                    </li>
                                    <li>
                                        Optional <strong>profile_image_url</strong>: must be a direct public image link
                                        (http/https). It is downloaded and saved to our storage. If the image cannot be
                                        downloaded, that row fails (nothing is updated) and the reason is shown in failed
                                        rows / CSV.
                                    </li>
                                </ul>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <a href={modeConfig.sampleUrl} className="btn btn-outline-primary" download="">
                                        {modeConfig.sampleLabel}
                                    </a>
                                </div>
                            </div>
                        )}

                        {mode === 'company' && (
                            <div className="alert alert-info">
                                <h5>Please Note:</h5>
                                <ul>
                                    <li>The file must be in Excel format (.xlsx). Max {MAX_ROWS} rows.</li>
                                    <li>
                                        <strong>Mandatory columns:</strong> uuid, institute_id
                                    </li>
                                    <li>
                                        If <strong>id</strong> (job id) is provided → update that job only (partial
                                        update for non-empty cells; empty cells leave existing DB values unchanged).
                                    </li>
                                    <li>
                                        If <strong>id</strong> is empty → insert a new job row for that uuid +
                                        institute_id. If the same job (same company, designation and start_date)
                                        already exists, the row fails and shows the existing job id to use instead.
                                    </li>
                                    <li>
                                        Columns map 1:1 to <code>job</code>: id, company_id, other_company,
                                        designation, start_date, end_date, industry_code, functional_area_code,
                                        seniority_level_code, job_role_code, job_category, is_current, is_outcome,
                                        is_campus_placement, is_eclectic, record_type_code, job_institute_id,
                                        other_institute, type_of_practice, type_of_institute, service_branch,
                                        project_link, country_code, city_code, is_remotely_working
                                    </li>
                                    <li>
                                        <code>institute_id</code> is only used to find the alumnus. For teaching jobs,
                                        put the employer institute id in <code>job_institute_id</code> or its name in{' '}
                                        <code>other_institute</code>.
                                    </li>
                                    <li>
                                        Filling <code>company_id</code> clears <code>other_company</code> and vice
                                        versa (same for <code>job_institute_id</code> / <code>other_institute</code>).
                                        Filling <code>end_date</code> without <code>is_current</code> marks the job as
                                        not current.
                                    </li>
                                    <li>
                                        <code>industry_code</code>, <code>functional_area_code</code> and{' '}
                                        <code>founder_status</code> must be a valid name or code.{' '}
                                        <code>record_type_code</code> (default <code>JOINED</code>) must be one of
                                        ENTREPRENEURSHIP, JOINED, GOT_OFFER_DID_NOT_JOIN, NO_OFFER. Dates must be real
                                        dates and start_date cannot be after end_date.
                                    </li>
                                    <li>
                                        Flag columns (<code>is_current</code>, etc.) must be <code>0</code> or{" "}
                                        <code>1</code> when filled.
                                    </li>
                                </ul>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <a href={modeConfig.sampleUrl} className="btn btn-outline-primary" download="">
                                        {modeConfig.sampleLabel}
                                    </a>
                                </div>
                            </div>
                        )}

                        {mode === 'achievement' && (
                            <div className="alert alert-info">
                                <h5>Please Note:</h5>
                                <ul>
                                    <li>The file must be in Excel format (.xlsx). Max {MAX_ROWS} rows.</li>
                                    <li>
                                        <strong>Mandatory columns:</strong> uuid, institute_id
                                    </li>
                                    <li>
                                        If <strong>achievement_id</strong> is provided → update that achievement only
                                        (partial update for non-empty cells).
                                    </li>
                                    <li>
                                        If <strong>achievement_id</strong> is empty → insert a new achievement for that
                                        alumni.
                                    </li>
                                    <li>
                                        Columns: achievement_id, type_code, title, description, achievement_date,
                                        level_code, weblink, program_category_code, notes
                                    </li>
                                    <li>
                                        <code>achievement_date</code> must be exactly <code>YYYY-MM-DD</code> (e.g.
                                        2024-03-15). Other formats will fail that row. Prefer Text format in Excel.
                                    </li>
                                </ul>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <a href={modeConfig.sampleUrl} className="btn btn-outline-primary" download="">
                                        {modeConfig.sampleLabel}
                                    </a>
                                </div>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} encType="multipart/form-data">
                            <div className="mb-3">
                                <label htmlFor="fileInput" className="form-label">
                                    Choose file (Only Excel, max {MAX_ROWS} rows)
                                </label>
                                <input
                                    className="form-control"
                                    type="file"
                                    id="fileInput"
                                    key={mode}
                                    accept=".xlsx"
                                    onChange={handleFileChange}
                                    required
                                />
                            </div>
                            <button type="submit" className="btn btn-primary" disabled={isLoading || !file}>
                                {isLoading ? modeConfig.loadingLabel : modeConfig.submitLabel}
                            </button>
                        </form>

                        {isLoading && (
                            <div className="progress mt-3">
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{ width: `${uploadProgress}%` }}
                                    aria-valuenow={uploadProgress}
                                    aria-valuemin={0}
                                    aria-valuemax={100}>
                                    {uploadProgress}%
                                </div>
                            </div>
                        )}

                        {errorMessage && (
                            <div className="alert alert-danger mt-4" role="alert">
                                {errorMessage}
                            </div>
                        )}

                        {responseMessage && (
                            <div className="alert alert-warning mt-4">
                                <h5>{modeConfig.summaryTitle}</h5>
                                <p>
                                    <strong>Successfully updated:</strong> {responseMessage.updated}
                                </p>
                                {modeConfig.showInserted && (
                                    <p>
                                        <strong>Successfully inserted:</strong> {responseMessage.inserted ?? 0}
                                    </p>
                                )}
                                <p>
                                    <strong>Failed:</strong> {responseMessage.failed}
                                </p>
                                {responseMessage.emailReport && (
                                    <div
                                        className={`alert ${
                                            responseMessage.emailReport.success ? 'alert-success' : 'alert-danger'
                                        } p-2`}>
                                        {responseMessage.emailReport.success
                                            ? `Report email sent to ${
                                                  responseMessage.emailReport.to || 'your login email'
                                              }`
                                            : `Report email NOT sent: ${
                                                  responseMessage.emailReport.message || 'broadcast/SendGrid failed'
                                              }`}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpdateAlumniLayout;
